/**
 * Conversation Notification Listener
 * 监听 Firebase 对话通知，处理新消息提醒
 */

import {
  collection,
  type DocumentChange,
  type FirestoreError,
  onSnapshot,
  query,
  type QuerySnapshot,
  type Unsubscribe,
  where,
  Timestamp,
  orderBy,
  limit,
} from 'firebase/firestore';
import { getFirebaseFirestore } from '@lib/config/firebase';
import { useConversationStore } from '@stores/conversationStore';
import type { ConversationNotification } from '../../../types/customers';

export interface ConversationListenerConfig {
  userId: string;
  onNotification?: (notification: ConversationNotification) => void;
  onError?: (error: FirestoreError) => void;
}

export interface ConversationListenerResult {
  unsubscribe: () => void;
}

/** `type` of the doc chatbot-api writes when a conversation's handoff mark changes (CS 2.0 需求 13). */
export const HANDOFF_CHANGED = 'handoff_changed';

/**
 * 创建对话通知监听器
 * 监听路径: users/{userId}/conversationNotifications
 */
export async function createConversationListener(
  config: ConversationListenerConfig
): Promise<ConversationListenerResult> {
  const { userId, onNotification, onError } = config;

  try {
    const db = getFirebaseFirestore();
    const collectionPath = `users/${userId}/externalActivationNotifications`;
    const notificationsCollection = collection(db, collectionPath);

    let isActive = true;
    let onSnapshotUnsubscribe: Unsubscribe | null = null;
    const currentUTCTimestamp = Date.now() - 5 * 60 * 1000;

    console.log('Setting up conversation notification listener:', {
      userId,
      collectionPath,
      currentUTCTimestamp,
      currentTime: new Date().toISOString(),
    });

    // 只监听未来的通知（从现在开始）- timestamp 是数字类型（毫秒）
    const futureQuery = query(notificationsCollection, where('timestamp', '>', currentUTCTimestamp.toString()));

    onSnapshotUnsubscribe = onSnapshot(
      futureQuery,
      {
        includeMetadataChanges: false,
        source: 'default',
      },
      (snapshot: QuerySnapshot) => {
        console.log('Conversation notification snapshot:', snapshot.docs.map(doc => doc.data()));
        if (!isActive) {
          console.log('Conversation listener is inactive, ignoring snapshot');
          return;
        }

        snapshot.docChanges().forEach((change: DocumentChange) => {
          console.log('----------Conversation notification change:', change);
          if (!isActive) return;

          // 只处理新添加的文档
          if (change.type === 'added') {
            const data = change.doc.data();
            const notification: ConversationNotification = {
              conversationId: data.conversationId,
              status: data.status || 'Pending',
              timestamp: Number(data.timestamp?.toMillis?.() || Date.now().toString()),
              type: typeof data.type === 'string' ? data.type : undefined,
            };

            console.log('Conversation notification received:', notification);

            // 处理通知
            handleNotification(notification);

            // 调用外部回调
            onNotification?.(notification);
          }
        });
      },
      (error: FirestoreError) => {
        console.error('Error listening for conversation notifications:', error);
        // 权限错误静默处理
        if (
          error.code === 'permission-denied' ||
          error.message.includes('Missing or insufficient permissions')
        ) {
          console.warn(
            'Firebase permission denied for conversation notifications. This is expected for some users.'
          );
          if (onSnapshotUnsubscribe) {
            onSnapshotUnsubscribe();
            onSnapshotUnsubscribe = null;
          }
          return;
        }

        console.error('Error listening for conversation notifications:', error);
        onError?.(error);
      }
    );

    console.log('Conversation notification listener started successfully');

    return {
      unsubscribe: () => {
        console.log('Unsubscribing conversation listener');
        isActive = false;
        if (onSnapshotUnsubscribe) {
          onSnapshotUnsubscribe();
        }
      },
    };
  } catch (error) {
    // 权限错误静默处理
    if (
      error instanceof Error &&
      error.message.includes('Missing or insufficient permissions')
    ) {
      console.warn(
        'Firebase setup permission denied for conversation listener. This is expected for some users.'
      );
      return {
        unsubscribe: () => {
          console.log('No-op unsubscribe (setup permission denied)');
        },
      };
    }

    console.error('Error setting up conversation listener:', error);
    throw error;
  }
}

/**
 * 处理通知逻辑
 * - 如果是当前选中的对话 → 触发消息刷新
 * - 如果是其他对话 → 标记紫点
 * - 如果状态是 pending → 标记可加入
 */
function handleNotification(notification: ConversationNotification): void {
  const store = useConversationStore.getState();
  const { conversationId, status } = notification;

  /*
   * CS 2.0 需求 13：转人工标记变了（升为紧急、顾客撤回……），不是一条新消息。它不带 status，走下面
   * 的通用分支会被默认成 Pending —— 行上亮紫点，还被标成「可加入」。只刷新列表（徽标跟着变），
   * 当前打开的那一段顺手刷新工单状态。
   */
  if (notification.type === HANDOFF_CHANGED) {
    if (store.shouldAutoRefreshMessages(conversationId)) {
      store.triggerTicketRefresh();
    }
    store.triggerListRefresh();
    return;
  }
  const statusLower = status?.toLowerCase();
  const isPending = statusLower === 'pending';
  const isClosed = statusLower === 'closed';

  // 判断是否是当前选中的对话
  const isCurrentConversation = store.shouldAutoRefreshMessages(conversationId);

  if (isCurrentConversation) {
    // 当前对话 → 触发消息刷新
    console.log('Auto-refreshing messages for current conversation:', conversationId);
    store.triggerMessageRefresh();

    // 当前对话收到通知 → 触发 ticket 刷新，更新右侧状态
    console.log('Triggering ticket refresh for current conversation:', conversationId);
    store.triggerTicketRefresh();
  }

  // pending 状态 或 非当前对话 → 标记紫点
  if (isPending || !isCurrentConversation) {
    console.log('Marking conversation as new:', conversationId, { isPending, isCurrentConversation });
    store.markAsNew(conversationId);
  }

  // 触发列表刷新（静默刷新）
  console.log('Triggering conversation list refresh');
  store.triggerListRefresh();

  // 如果状态是 pending，标记可加入
  if (isPending) {
    console.log('Marking conversation as pending join:', conversationId);
    store.markAsPendingJoin(conversationId);
  }
}

/**
 * CS 2.0 需求 13：同一个集合，只当「有事发生了」的信号用。
 *
 * <p>给导航层的红点计数用（`useHandoffSummarySource`），所以它<b>不碰 conversationStore</b>：
 * 不标紫点、不触发列表刷新、不标可加入 —— 那些是 {@link createConversationListener} 的事，
 * 它只在 Anna Chat 页里开。两个同时开着时，若这一个也走 `handleNotification`，每条通知会被处理
 * 两遍。这里每来一条新文档（新消息、转人工、`handoff_changed`）就回调一次，由调用方去重拉计数。
 *
 * <p>同样的「只看从现在起」查询（`timestamp` 是毫秒数的字符串，按字符串比较），同样静默吞掉
 * 权限错误。
 */
/** What a new notification doc says, as far as the handoff signal cares. */
export interface HandoffSignalDoc {
  type?: string;
  /** On a `handoff_changed` doc: `requested`, `urgent` or `withdrawn`. */
  status?: string;
  conversationId?: string;
  /** Epoch millis as a string, as chatbot-api writes it. */
  timestamp?: string;
}

export async function startHandoffSignalListener(
  userId: string,
  onSignal: (added: HandoffSignalDoc[]) => void
): Promise<ConversationListenerResult> {
  try {
    const db = getFirebaseFirestore();
    const notifications = collection(db, `users/${userId}/externalActivationNotifications`);
    const since = (Date.now() - 5 * 60 * 1000).toString();
    let active = true;
    let unsubscribe: Unsubscribe | null = onSnapshot(
      query(notifications, where('timestamp', '>', since)),
      snapshot => {
        if (!active) return;
        const added = snapshot
          .docChanges()
          .filter(change => change.type === 'added')
          .map(change => change.doc.data() as HandoffSignalDoc);
        if (added.length > 0) onSignal(added);
      },
      error => {
        // Same as the conversation listener: some accounts have no read rule here. The poll and
        // the focus refresh still keep the count fresh, so this degrades rather than breaks.
        if (error.code !== 'permission-denied') {
          console.error('[HandoffSignal] listener error:', error);
        }
        unsubscribe?.();
        unsubscribe = null;
      }
    );
    return {
      unsubscribe: () => {
        active = false;
        unsubscribe?.();
        unsubscribe = null;
      },
    };
  } catch (error) {
    console.warn('[HandoffSignal] listener not started:', error);
    return { unsubscribe: () => undefined };
  }
}

/**
 * 便捷函数：为用户启动监听器
 */
export function startConversationListener(
  userId: string,
  callbacks?: {
    onNotification?: (notification: ConversationNotification) => void;
    onError?: (error: FirestoreError) => void;
  }
): Promise<ConversationListenerResult> {
  return createConversationListener({
    userId,
    onNotification: callbacks?.onNotification,
    onError: callbacks?.onError,
  });
}
